import { useGlobalAlert } from "contexts/GlobalAlertContext"
import FormData from "form-data"
import { useState } from "react"
import { studentRequest, coreRequest } from "services/requests"
import storage from "utils/persistentStorage"
import { LOCAL_STORAGE_PREFIX } from "common/constants"

const prefix = "account"

const useApi = () => {
  const [isFetching, setIsFetching] = useState(false)
  const { showAlert } = useGlobalAlert()

  const getUserAllInformation = async () => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/allData`,
      method: "get",
    })
    if (!response.success) showAlert(response.message)
    setIsFetching(false)
    return response.data
  }

  const verifyStatus = async () => {
    try {
      const response = await studentRequest.sendRequest({
        endpoint: `${prefix}/verifyStatus?appVersion=${process.env.REACT_APP_VERSION}`,
        method: "get",
      })
      console.log(`Account status feched successfully: ${response.success}`)
    } catch (error) {}
  }

  const getProviders = async ({ userId = 0 }) => {
    const cacheKey = `${LOCAL_STORAGE_PREFIX}global_providers_${userId}`
    const cachedData = await storage.getItem(cacheKey)
    if (cachedData) return cachedData

    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `externalProviders`,
      method: "get",
    })
    if (response.success) {
      const data = response?.data || []
      await storage.setItem(cacheKey, data)
      setIsFetching(false)
      return data
    }

    if (!response.success) showAlert(response.message)
    setIsFetching(false)
    return []
  }

  const updateUserAvatar = async ({ avatar }) => {
    setIsFetching(true)
    const data = new FormData()
    data.append("avatar", avatar)
    const response = await studentRequest.sendFormData({
      endpoint: `${prefix}/avatar`,
      formData: data,
    })
    if (!response.success) showAlert(response?.message)
    setIsFetching(false)
    return response.success
  }

  const updateUserInformation = async ({
    name,
    lastName,
    username,
    languageId,
    avatar,
    password,
  }) => {
    setIsFetching(true)
    const data = new FormData()
    if (avatar) {
      data.append("avatar", avatar)
    }
    data.append("name", name)
    data.append("lastName", lastName)
    data.append("username", username)
    data.append("languageId", languageId)
    if (password) {
      data.append("password", password)
    }

    const response = await studentRequest.sendFormData({
      endpoint: `${prefix}/edit`,
      formData: data,
    })
    setIsFetching(false)
    return response?.data
  }

  const getStudentLicenses = async () => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `memberships/licenses`,
      method: "get",
    })
    if (!response.success) showAlert(response.message)
    setIsFetching(false)
    return response.data
  }

  const updateUserAudioSettings = async ({
    enabledAudioEffects,
    volume,
    playBackRate,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `mediaSettings/audio/update`,
      body: {
        enabledAudioEffects,
        volume,
        playBackRate,
      },
      method: "post",
    })
    if (!response.success) showAlert(response.message)
    setIsFetching(false)
    return response.data
  }

  const setIsConfigurationCompleted = async ({ isConfigurationCompleted }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/configured/${isConfigurationCompleted}`,
      method: "post",
    })
    if (!response.success) showAlert(response.message)
    setIsFetching(false)
    return response.data
  }

  const updateUserPassword = async ({ oldPassword, newPassword }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `auth/setPassword`,
      body: {
        oldPassword,
        newPassword,
      },
      method: "post",
    })

    setIsFetching(false)
    return response
  }

  const createAccountWithProvider = async ({
    externalProviderUserId,
    token,
    givenName,
    familyName,
    username,
    groupId,
    b2CBranchId,
    languageId,
    languageToLearn,
    pictograms,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `externalProviders/createAccount`,
      body: {
        externalProviderUserId,
        token,
        givenName,
        familyName,
        username,
        groupId,
        languageId,
        ...(pictograms !== undefined && { pictograms }),
        ...(b2CBranchId > 0 && { b2CBranchId }),
        ...(b2CBranchId > 0 && { languageToLearn }),
      },
      method: "post",
    })
    // update current token
    setIsFetching(false)
    return response
  }

  const linkAccountWithProvider = async ({
    externalProviderUserId,
    token,
    provider,
    givenName,
    familyName,
    username,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `externalProviders/link`,
      body: {
        externalProviderUserId,
        token,
        provider,
        givenName,
        familyName,
        username,
      },
      method: "post",
    })
    // update current token
    setIsFetching(false)
    return response
  }

  const removeExternalProvider = async ({ provider }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `externalProviders/unlink/${provider}`,
      body: { provider },
      method: "post",
    })
    setIsFetching(false)
    return response
  }

  const unlinkAccountExternalProvider = async ({
    name,
    lastName,
    username,
    languageId,
    password,
    provider,
  }) => {
    setIsFetching(true)
    const response = await updateUserInformation({
      name,
      lastName,
      username,
      languageId,
      password,
    })
    if (response?.status) {
      await removeExternalProvider({ provider })
      setIsFetching(false)
      return {
        status: true,
      }
    } else {
      setIsFetching(false)
      return response
    }
  }

  const getUserTextSettings = async () => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `mediaSettings/textEditor`,
      body: null,
      method: "get",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data
  }

  const updateUserTextSettings = async ({
    fontSize = 1,
    fontFamily = 0,
    backgroundColor = 0,
    fillColor = 0,
    ruleEnabled = true,
  }) => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `mediaSettings/textEditor/update`,
      body: {
        fontSize,
        fontFamily,
        backgroundColor,
        fillColor,
        ruleEnabled,
      },
      method: "post",
    })
    if (!response.success) console.log(response.message)
    setIsFetching(false)
    return response.data
  }

  const setAllowTutorials = async ({ allow }) => {
    setIsFetching(true)
    const response = await coreRequest.sendRequest({
      endpoint: `tutorials/setAllowTutorials/${allow}`,
      body: {},
      method: "post",
    })
    if (!response.success) showAlert(response.message)
    setIsFetching(false)
    return response.success
  }

  const getDemo = async () => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/demo`,
      body: {},
      method: "get",
    })
    setIsFetching(false)
    return response.data
  }

  const sendDemoSurvey = async ({ survey }) => {
    setIsFetching(true)
    const data = JSON.stringify(survey)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/demo/survey`,
      body: data,
      method: "post",
    })
    setIsFetching(false)
    return response.data
  }

  const getSessionState = async () => {
    setIsFetching(true)
    const response = await studentRequest.sendRequest({
      endpoint: `${prefix}/sessionState`,
      body: null,
      method: "get",
    })
    if (!response.success) showAlert(response.message)
    setIsFetching(false)
    return response?.data
  }

  const exchangeToken = async () => {
    setIsFetching(true)
    const response = await fetch("/auth.aspx/exchange", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
    const data = await response.json()
    const responseData = data.d
    // transform json string to object
    const json =
      typeof responseData === "string" ? JSON.parse(responseData) : responseData
    const { success } = json
    if (!success) console.log(json.message)
    setIsFetching(false)
    return json
  }

  return {
    isFetching,
    getSessionState,
    exchangeToken,
    getUserAllInformation,
    verifyStatus,
    getProviders,
    updateUserAvatar,
    updateUserInformation,
    getStudentLicenses,
    updateUserAudioSettings,
    setIsConfigurationCompleted,
    updateUserPassword,
    createAccountWithProvider,
    linkAccountWithProvider,
    removeExternalProvider,
    unlinkAccountExternalProvider,
    getUserTextSettings,
    updateUserTextSettings,
    setAllowTutorials,
    getDemo,
    sendDemoSurvey,
  }
}

export default useApi
